import React, { useState } from "react";
import emailjs from 'emailjs-com';
import { motion } from "framer-motion";
import { BarChart3, ShieldCheck, X, Layers, FolderClosed, Settings2, ListTodo, ActivitySquare, Users, MonitorSmartphone, Smartphone } from "lucide-react";
import { Mail, Phone, MapPin, Linkedin, Menu } from "lucide-react";
import "./styles/globals.css";
import VisionSection from "./components/VisionSection.js"

const motionConfig = {
  initial: { opacity: 0, y: 50 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8 },
};

const CustomButton = ({ className, children, ...props }) => (
  <motion.button
    className={`${className} px-6 py-3 text-base text-lg rounded-full shadow-lg font-ageo transition-transform transform hover:scale-105`}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
    {...props}
  >
    {children}
  </motion.button>
);

const getCurrentYear = () => new Date().getFullYear();

const services = [
 {
    icon: <ListTodo size={40} />, title: "PMO & Program Consulting", description: "Project leadership for biotech teams", details: "We support PMO design, cross-functional planning, and execution across R&D discovery and clinical programs."
  },
  {
    icon: <BarChart3 size={40} />, title: "Dashboards & Reporting", description: "Real-time project visibility", details: "We deliver dashboards to track milestones, timelines, budgets, and portfolio health — built in Smartsheet, Power BI, or Excel."
  },
  {
    icon: <FolderClosed size={40} />, title: "SharePoint Architecture", description: "Organized and secure file structures", details: "We build scalable SharePoint sites with department and project templates, permission models, and structured folder systems."
  },
  {
    icon: <Layers size={40} />, title: "SharePoint & Teams Integration", description: "Connected collaboration environments", details: "We align SharePoint libraries and Teams channels for seamless access, collaboration, and governance."
  },
  {
    icon: <Settings2 size={40} />, title: "Smartsheet Automation", description: "Custom workflows that scale", details: "We configure Smartsheet to your organizational needs using custom formulas, logic-based automation, and integrations."
  },
  {
    icon: <Users size={40} />, title: "Resource Management", description: "Plan and track hours across projects", details: "We set up Smartsheet Resource Management systems for resource planning, timecard tracking, and utilization reporting."
  },
  {
    icon: <ShieldCheck size={40} />, title: "Governance & Compliance", description: "Clarity, structure, and control", details: "We create frameworks and documentation systems that support audit-readiness, access control, and project oversight."
  },
  {
    icon: <ActivitySquare size={40} />, title: "Operational Insights", description: "Drive improvement through data", details: "We surface metrics that reveal gaps, reduce delays, and guide process improvements."
  },
  {
    icon: <MonitorSmartphone size={40} />, title: "Web Development", description: "Custom-built web solutions", details: "We design and develop modern, responsive websites tailored to biotech needs — from branding to secure infrastructure."
  },
  {
    icon: <Smartphone size={40} />, title: "Mobile Applications", description: "Tools at your fingertips", details: "We build mobile apps that extend your systems into the field — enabling real-time access, data entry, and collaboration."
  }
];


export const ServicesSection = () => (
  <section id="services" className="bg-white py-24 px-6">
    <motion.div className="text-center mb-16" {...motionConfig}>
      <h2 className="text-5xl font-bold text-dark-teal">Our Services</h2>
      <p className="text-gray-600 mt-6 text-lg max-w-2xl mx-auto">
        BioPM delivers expert systems and consulting to accelerate project execution in drug discovery and clinical stage biotech companies.
      </p>
    </motion.div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
      {services.map((service, index) => (
        <div key={index} className="flex items-start gap-6">
          <div className="text-teal-green">{service.icon}</div>
          <div>
            <h3 className="text-2xl font-bold text-night-sky mb-2">{service.title}</h3>
            <p className="text-gray-600 text-base">{service.details}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

export default function BioPMWebsite() {
  const handleSubmit = (e) => {
    e.preventDefault();
    emailjs.sendForm(
      'service_w9ntyqt', // replace with your EmailJS service ID
      'template_54kwv8j', // replace with your EmailJS template ID
      e.target,
      'a1q0fJffNbDQKyOje' // replace with your EmailJS user/public key
    ).then(() => {
      alert('Form submitted successfully!');
      setShowWaitlistForm(false);
    }).catch((error) => {
      console.error('Email error:', error);
      alert('There was an error submitting the form.');
    });
  };
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const handleContactSubmit = (e) => {
    e.preventDefault();
    emailjs.sendForm(
      'service_w9ntyqt',
      'template_k2nfhm7',
      e.target,
      'a1q0fJffNbDQKyOje'
    ).then(() => {
      alert('Message sent successfully!');
      setShowContactForm(false);
    }).catch((error) => {
      console.error('Email error:', error);
      alert('There was an error sending your message.');
    });
  };
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLearnMore = (event) => {
    event.preventDefault();
    const aboutSection = document.getElementById("about");
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="bg-white text-night-sky font-ageo">

    {/* Navigation Menu */}
      <nav className="fixed top-0 left-0 w-full bg-white shadow-md py-4 px-6 flex justify-between items-center z-50">
        <a href="" className="text-2xl font-bold text-dark-teal cursor-pointer">
          <span className="text-dark-teal">Bio</span>
          <span className="text-teal-green">PM</span>
        </a>
        <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)}>
          <Menu size={28} />
        </button>
        <ul className={`lg:flex gap-6 text-lg font-semibold ${menuOpen ? 'block' : 'hidden'} lg:block`}>
          <li><a href="#about" className="hover:text-teal-green">About</a></li>
          <li><a href="#services" className="hover:text-teal-green">Services</a></li>
          <li><a href="/ai-readiness" className="hover:text-teal-green">AI Readiness™</a></li>
          <li><a href="#curepath" className="hover:text-teal-green">CurePath™</a></li>
          <li><a href="#vision" className="hover:text-teal-green">Vision</a></li>
          <li><a href="#contact" className="hover:text-teal-green">Contact</a></li>
        </ul>
      </nav>

      {/* Side Drawer */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50">
          <div className="bg-white w-64 h-full shadow-lg p-6 relative">
            <button className="absolute top-4 right-4 text-gray-600 hover:text-gray-900" onClick={() => setMenuOpen(false)}>
              <X size={24} />
            </button>
            <ul className="mt-10 space-y-6 text-lg font-semibold">
              <li><a href="#about" className="hover:text-teal-green" onClick={() => setMenuOpen(false)}>About</a></li>
              <li><a href="#services" className="hover:text-teal-green" onClick={() => setMenuOpen(false)}>Services</a></li>
              <li><a href="/ai-readiness" className="hover:text-teal-green" onClick={() => setMenuOpen(false)}>AI Readiness™</a></li>
              <li><a href="#curepath" className="hover:text-teal-green" onClick={() => setMenuOpen(false)}>CurePath™</a></li>
              <li><a href="#vision" className="hover:text-teal-green" onClick={() => setMenuOpen(false)}>Vision</a></li>
              <li><a href="#contact" className="hover:text-teal-green" onClick={() => setMenuOpen(false)}>Contact</a></li>
            </ul>
          </div>
        </div>
      )}

      {/* Hero Section - Optimized Visuals */}
      <section id="hero" className="relative bg-gradient-to-r from-teal-green to-dark-teal text-white py-20 md:py-28 lg:py-32 px-8 text-center flex flex-col items-center justify-center min-h-screen overflow-hidden">
        <motion.div {...motionConfig} className="relative z-10">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold drop-shadow-xl leading-tight">Enterprise Solutions for Drug Development</h1>
          <p className="mt-6 text-2xl max-w-3xl mx-auto font-light drop-shadow-lg tracking-wide">
          Empowering project teams and project management offices to drive efficiency, streamline workflows, maintain oversight, and accelerate drug development from discovery to approval.
          </p>  
          <div className="mt-12 flex gap-6 justify-center">
  <CustomButton className="bg-white text-teal-green hover:bg-gray-200 w-full sm:w-auto" onClick={() => setShowContactForm(true)}>Get Started</CustomButton>
  <CustomButton className="bg-dark-teal text-white hover:bg-teal-green w-full sm:w-auto" onClick={handleLearnMore}>Learn More</CustomButton>
</div>
        </motion.div>
        {/* Background Overlay */}
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-transparent to-black opacity-30"></div>
      </section>

      {/* About BioPM Section */}
      <section id="about" className="bg-gray-100 py-32 px-8 text-center">
        <motion.div {...motionConfig}>
          <h2 className="text-5xl font-bold text-night-sky">
          <span className="text-dark-teal">About </span>
          <span className="text-dark-teal">Bio</span>
          <span className="text-teal-green">PM</span>
        </h2>
          <p className="mt-6 text-xl max-w-4xl mx-auto text-gray-600">
            Founded and led by industry expert <a href="https://www.linkedin.com/in/marcrnelson/" target="_blank" rel="noopener noreferrer" className="text-dark-teal hover:no-underline"><strong>Marc R. Nelson, MPS, PMP</strong></a>, BioPM is dedicated to revolutionizing project management systems and processes utilized in drug development. With decades of experience spanning strategic leadership, project and resource management, and innovative technology solutions, BioPM is at the forefront of streamlining the path to market for life-changing therapies.
          </p>
          <p className="mt-6 text-xl max-w-3xl mx-auto text-gray-600">
            Our flagship platform currently in development, <a href="#curepath" className="text-dark-teal hover:no-underline"><strong>CurePath™</strong></a>, will be used to deliver cutting-edge solutions tailored for drug development teams, integrating AI-driven insights, regulatory compliance tracking, and scalable project oversight tools. BioPM is committed to empowering organizations with efficient, data-driven decision-making strategies that accelerate drug discovery and clinical programs.
          </p>
        </motion.div>
      </section>

      {/* Services Section */}
      <section id="services" className="bg-white py-24 px-6">
  <motion.div className="text-center mb-16" {...motionConfig}>
    <h2 className="text-5xl font-bold text-dark-teal">Our Services</h2>
    <p className="text-gray-600 mt-6 text-2xl max-w-2xl mx-auto">
      BioPM offers expert systems development and consulting services to accelerate project execution in drug discovery and clinical stage biotech companies.
    </p>
  </motion.div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
    {services.map((service, index) => (
      <div key={index} className="flex items-start gap-6">
        <div className="text-teal-green">{service.icon}</div>
        <div>
          <h3 className="text-2xl font-bold text-night-sky mb-2">{service.title}</h3>
          <p className="text-gray-600 text-xl">{service.details}</p>
        </div>
      </div>
    ))}
  </div>
</section>

      {/* AI Readiness Section */}
      <section id="ai-readiness" className="bg-dark-teal text-white py-28 px-8">
        <motion.div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" {...motionConfig}>
          <div>
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-teal-green/40 bg-teal-green/10 text-teal-green text-sm font-bold mb-6">
              BioPM AI Readiness Assessment™
            </div>

            <h2 className="text-5xl font-bold leading-tight">
              Turn AI uncertainty into an executive transformation plan.
            </h2>

            <p className="mt-6 text-xl text-white/70 leading-relaxed">
              Assess your organization across Data, Technology, Governance, Organization, and Delivery — then receive an Executive Dashboard™, Executive Report™, Executive Deck™, and 90-Day Transformation Plan™.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "50-question executive assessment",
                "AI Readiness Heatmap™",
                "Executive Report™ PDF",
                "Executive Deck™ PowerPoint",
                "90-Day Transformation Plan™",
                "CurePath™ execution pathway",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 text-white/85">
                  <span className="w-3 h-3 rounded-full bg-teal-green inline-block" />
                  <span className="font-semibold">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a href="/ai-readiness" className="inline-flex justify-center px-8 py-4 rounded-full bg-teal-green text-white font-bold no-underline hover:bg-white hover:text-dark-teal transition">
                Explore AI Readiness™
              </a>
              <a href="mailto:contact@biopm.com?subject=AI%20Readiness%20Assessment%20Inquiry" className="inline-flex justify-center px-8 py-4 rounded-full border border-white/20 text-white font-bold no-underline hover:bg-white hover:text-dark-teal transition">
                Request Assessment
              </a>
            </div>
          </div>

          <div className="bg-white text-dark-teal rounded-3xl p-8 shadow-2xl">
            <div className="text-sm font-bold text-gray-400 uppercase mb-4">
              Sample Executive Output
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                ["Current Readiness", "60"],
                ["Target Readiness", "81"],
                ["Captured Value", "+4"],
                ["Remaining Value", "+17"],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl bg-gray-50 border border-gray-100 p-5">
                  <div className="text-xs font-bold text-gray-400 uppercase">{label}</div>
                  <div className="text-3xl font-extrabold text-dark-teal mt-2">{value}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl bg-gray-50 border border-gray-100 p-5">
              <div className="text-xs font-bold text-gray-400 uppercase mb-3">AI Readiness Heatmap™</div>
              {[
                ["Data", "50"],
                ["Technology", "55"],
                ["Governance", "65"],
                ["Organization", "68"],
                ["Delivery", "65"],
              ].map(([domain, score]) => (
                <div key={domain} className="flex justify-between py-2 border-b last:border-b-0">
                  <span className="font-bold">{domain}</span>
                  <span className="font-extrabold text-teal-green">{score}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* CurePath Section (Refined Design with Separate Waitlist Modal) */}
      <section id="curepath" className="bg-gradient-to-b from-gray-100 to-white py-32 px-8 text-center">
  <motion.div {...motionConfig}>
    <div className="flex justify-center items-center gap-4 mb-6">
      <img src="/curepath_primary-with-trademark-logo-full-color-rgb-864px@72ppi.png" alt="CurePath Logo" width="250" height="70" />
    </div>
    <p className="mt-6 text-2xl max-w-4xl mx-auto text-gray-600">
      CurePath™ is BioPM’s upcoming project execution platform being designed specifically for drug development. Currently in development, CurePath will help teams plan, track, and deliver with clarity and control.
    </p>
    <p className="mt-4 text-xl max-w-3xl mx-auto text-gray-600">
      From timelines to dashboards, CurePath will simplify complexity — with features tailored for drug development operations.
    </p>
    <div className="mt-10 flex gap-6 justify-center">
      <CustomButton className="bg-teal-green text-white hover:bg-dark-teal" onClick={() => setShowWaitlistForm(true)}>
        Join the Waitlist
      </CustomButton>
    </div>
  </motion.div>
</section>
      
      {/* Waitlist Modal */}
      {showWaitlistForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg w-96 relative">
            <button className="absolute top-4 right-4 text-gray-600 hover:text-gray-900" onClick={() => setShowWaitlistForm(false)}>
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold text-dark-teal text-center">Join the CurePath™ Waitlist</h2>
            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <input name="user_name" type="text" placeholder="Full Name" className="w-full p-3 border rounded" required />
              <input
                name="user_email"
                type="email"
                placeholder="Email Address"
                className="w-full p-3 border rounded"
                pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                title="Please enter a valid email address"
                required
              />
              <input name="organization" type="text" placeholder="Name of Organization" className="w-full p-3 border rounded" required />
              <input name="location" type="text" placeholder="City, State" className="w-full p-3 border rounded" required />
              <select name="country" className="w-full p-3 border rounded" required>
                <option value="">Country</option>
                <option>United States</option>
                <option>Afghanistan</option>
                <option>Albania</option>
                <option>Algeria</option>
                <option>Andorra</option>
                <option>Angola</option>
                <option>Antigua and Barbuda</option>
                <option>Argentina</option>
                <option>Armenia</option>
                <option>Australia</option>
                <option>Austria</option>
                <option>Azerbaijan</option>
                <option>Bahamas</option>
                <option>Bahrain</option>
                <option>Bangladesh</option>
                <option>Barbados</option>
                <option>Belarus</option>
                <option>Belgium</option>
                <option>Belize</option>
                <option>Benin</option>
                <option>Bhutan</option>
                <option>Bolivia</option>
                <option>Bosnia and Herzegovina</option>
                <option>Botswana</option>
                <option>Brazil</option>
                <option>Brunei</option>
                <option>Bulgaria</option>
                <option>Burkina Faso</option>
                <option>Burundi</option>
                <option>Cambodia</option>
                <option>Cameroon</option>
                <option>Canada</option>
                <option>Cape Verde</option>
                <option>Central African Republic</option>
                <option>Chad</option>
                <option>Chile</option>
                <option>China</option>
                <option>Colombia</option>
                <option>Comoros</option>
                <option>Congo (Brazzaville)</option>
                <option>Congo (Kinshasa)</option>
                <option>Costa Rica</option>
                <option>Croatia</option>
                <option>Cuba</option>
                <option>Cyprus</option>
                <option>Czech Republic</option>
                <option>Denmark</option>
                <option>Djibouti</option>
                <option>Dominica</option>
                <option>Dominican Republic</option>
                <option>Ecuador</option>
                <option>Egypt</option>
                <option>El Salvador</option>
                <option>Equatorial Guinea</option>
                <option>Eritrea</option>
                <option>Estonia</option>
                <option>Eswatini</option>
                <option>Ethiopia</option>
                <option>Fiji</option>
                <option>Finland</option>
                <option>France</option>
                <option>Gabon</option>
                <option>Gambia</option>
                <option>Georgia</option>
                <option>Germany</option>
                <option>Ghana</option>
                <option>Greece</option>
                <option>Grenada</option>
                <option>Guatemala</option>
                <option>Guinea</option>
                <option>Guinea-Bissau</option>
                <option>Guyana</option>
                <option>Haiti</option>
                <option>Honduras</option>
                <option>Hungary</option>
                <option>Iceland</option>
                <option>India</option>
                <option>Indonesia</option>
                <option>Iran</option>
                <option>Iraq</option>
                <option>Ireland</option>
                <option>Israel</option>
                <option>Italy</option>
                <option>Jamaica</option>
                <option>Japan</option>
                <option>Jordan</option>
                <option>Kazakhstan</option>
                <option>Kenya</option>
                <option>Kiribati</option>
                <option>Kuwait</option>
                <option>Kyrgyzstan</option>
                <option>Laos</option>
                <option>Latvia</option>
                <option>Lebanon</option>
                <option>Lesotho</option>
                <option>Liberia</option>
                <option>Libya</option>
                <option>Liechtenstein</option>
                <option>Lithuania</option>
                <option>Luxembourg</option>
                <option>Madagascar</option>
                <option>Malawi</option>
                <option>Malaysia</option>
                <option>Maldives</option>
                <option>Mali</option>
                <option>Malta</option>
                <option>Marshall Islands</option>
                <option>Mauritania</option>
                <option>Mauritius</option>
                <option>Mexico</option>
                <option>Micronesia</option>
                <option>Moldova</option>
                <option>Monaco</option>
                <option>Mongolia</option>
                <option>Montenegro</option>
                <option>Morocco</option>
                <option>Mozambique</option>
                <option>Myanmar</option>
                <option>Namibia</option>
                <option>Nauru</option>
                <option>Nepal</option>
                <option>Netherlands</option>
                <option>New Zealand</option>
                <option>Nicaragua</option>
                <option>Niger</option>
                <option>Nigeria</option>
                <option>North Korea</option>
                <option>North Macedonia</option>
                <option>Norway</option>
                <option>Oman</option>
                <option>Pakistan</option>
                <option>Palau</option>
                <option>Palestine</option>
                <option>Panama</option>
                <option>Papua New Guinea</option>
                <option>Paraguay</option>
                <option>Peru</option>
                <option>Philippines</option>
                <option>Poland</option>
                <option>Portugal</option>
                <option>Qatar</option>
                <option>Romania</option>
                <option>Russia</option>
                <option>Rwanda</option>
                <option>Saint Kitts and Nevis</option>
                <option>Saint Lucia</option>
                <option>Saint Vincent and the Grenadines</option>
                <option>Samoa</option>
                <option>San Marino</option>
                <option>Sao Tome and Principe</option>
                <option>Saudi Arabia</option>
                <option>Senegal</option>
                <option>Serbia</option>
                <option>Seychelles</option>
                <option>Sierra Leone</option>
                <option>Singapore</option>
                <option>Slovakia</option>
                <option>Slovenia</option>
                <option>Solomon Islands</option>
                <option>Somalia</option>
                <option>South Africa</option>
                <option>South Korea</option>
                <option>South Sudan</option>
                <option>Spain</option>
                <option>Sri Lanka</option>
                <option>Sudan</option>
                <option>Suriname</option>
                <option>Sweden</option>
                <option>Switzerland</option>
                <option>Syria</option>
                <option>Taiwan</option>
                <option>Tajikistan</option>
                <option>Tanzania</option>
                <option>Thailand</option>
                <option>Timor-Leste</option>
                <option>Togo</option>
                <option>Tonga</option>
                <option>Trinidad and Tobago</option>
                <option>Tunisia</option>
                <option>Turkey</option>
                <option>Turkmenistan</option>
                <option>Tuvalu</option>
                <option>Uganda</option>
                <option>Ukraine</option>
                <option>United Arab Emirates</option>
                <option>United Kingdom</option>
                <option>United States</option>
                <option>Uruguay</option>
                <option>Uzbekistan</option>
                <option>Vanuatu</option>
                <option>Vatican City</option>
                <option>Venezuela</option>
                <option>Vietnam</option>
                <option>Yemen</option>
                <option>Zambia</option>
                <option>Zimbabwe</option>
              </select>
              <select name="size" className="w-full p-3 border rounded" required>
                <option value="">Size of Organization</option>
                <option>1-10 employees</option>
                <option>11-50 employees</option>
                <option>51-200 employees</option>
                <option>201-500 employees</option>
                <option>501-1,000 employees</option>
                <option>1,001-5,000 employees</option>
                <option>5,001-10,000 employees</option>
                <option>10,001+ employees</option>
              </select>
              <textarea name="features" placeholder="Features you'd like to see" className="w-full p-3 border rounded h-24" required></textarea>
              <CustomButton className="bg-teal-green text-white w-full">Submit</CustomButton>
            </form>
          </div>
        </div>
      )}

      {/* Vision Section */}
      <VisionSection />

      {/* Call to Action Section */}
      <section className="bg-gradient-to-r from-teal-green to-dark-teal text-white py-32 px-8 text-center flex flex-col items-center">
        <motion.div {...motionConfig}>
          <h2 className="text-5xl font-bold drop-shadow-lg">Ready to Transform Your Drug Development Operations?</h2>
          <p className="mt-6 text-2xl max-w-3xl mx-auto font-light">
            Partner with BioPM today and experience the future of innovation, efficiency, and success in bringing important life-changing therapies to patients in need.
          </p>
        </motion.div>
        <div className="mt-10 flex gap-6">
          <CustomButton className="bg-white text-teal-green hover:bg-gray-100" onClick={handleLearnMore}>Learn More</CustomButton>
          <CustomButton className="bg-dark-teal text-white hover:bg-teal-green" onClick={() => setShowContactForm(true)}>
            Get Started
          </CustomButton>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg w-96 relative">
            <button className="absolute top-4 right-4 text-gray-600 hover:text-gray-900" onClick={() => setShowContactForm(false)}>
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold text-dark-teal text-center">Get Started with BioPM</h2>
            <form className="mt-6 space-y-4" onSubmit={handleContactSubmit}>
              <input name="user_name" type="text" placeholder="Full Name" className="w-full p-3 border rounded" required />
              <input
                name="user_email"
                type="email"
                placeholder="Email Address"
                className="w-full p-3 border rounded"
                pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                title="Please enter a valid email address"
                required
              />
              <input name="organization" type="text" placeholder="Organization" className="w-full p-3 border rounded" required />
              <textarea placeholder="Tell us about your needs" className="w-full p-3 border rounded h-24" required></textarea>
              <CustomButton className="bg-teal-green text-white w-full">Submit</CustomButton>
            </form>
          </div>
        </div>
      )}

      {/* Footer Section */}
      <footer id="contact" className="bg-dark-teal text-white py-16 px-8 text-center">
        <div className="container mx-auto">
          <h3 className="text-3xl font-semibold">Contact Us</h3>
          <div className="flex flex-col items-center mt-6 space-y-4 text-lg">
            <div className="flex items-center gap-3">
              <Mail size={24} />
              <span>info@biopm.com</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone size={24} />
              <span>+1 (858) 688-7497</span>
            </div>
            <div className="flex items-center gap-3">
              <MapPin size={24} />
              <span>Henderson, NV</span>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <a href="https://www.linkedin.com/company/bio-pm" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-gray-300">
                <Linkedin size={24} />
                <span>Follow us on LinkedIn</span>
              </a>
            </div>
          </div>
          <p className="text-sm mt-6">&copy; {getCurrentYear()} BioPM, Inc. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}
